"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { projects, type ProjectCard } from "@/data/projects";
import { HeroCtaButton } from "@/components/ui/hero-cta-button";
import { sectionSurface, type SectionVariant } from "@/lib/section-theme";

const HERO_GRADIENT_TEXT =
  "bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent";

const SECTION_TITLE_CLASS =
  "mx-auto max-w-4xl text-balance text-[1.5rem] font-bold leading-[1.08] tracking-[-0.04em] sm:text-3xl md:text-4xl md:leading-[1.05]";
const PORTFOLIO_THUMB_SIZES =
  "(max-width: 767px) calc(100vw - 6rem), calc((min(100vw, 72rem) - 15rem) / 3)";

const SEQUENCE_EASE = [0.16, 1, 0.3, 1] as const;
const CARD_STAGGER = 0.08;
const GRID_DELAY = 0.14;

function entranceTransition(
  reducedMotion: boolean | null,
  delay = 0,
  duration = 0.55,
) {
  if (reducedMotion) return { duration: 0 };
  return { delay, duration, ease: SEQUENCE_EASE };
}

const CARD_SHELL_LIGHT =
  "group relative flex flex-col gap-5 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm transition-all duration-300 hover:border-emerald-200 hover:shadow-md sm:gap-6 sm:rounded-3xl sm:p-6";

const CARD_SHELL_DARK =
  "group relative flex flex-col gap-6 rounded-3xl p-6 transition-all duration-300 bg-gradient-to-r from-[#71DDAE] to-[#2A9D7A] shadow-[0_12px_40px_rgba(113,221,174,0.22)] hover:shadow-[0_20px_50px_rgba(113,221,174,0.32)]";

const THUMB_INTERACTIVE_LIGHT =
  "relative block w-full cursor-pointer overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 outline-none transition-all duration-300 hover:border-emerald-200 hover:shadow-[0_12px_32px_-16px_rgba(16,185,129,0.35)] focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f3f3f3]";

const THUMB_INTERACTIVE_DARK =
  "relative block w-full cursor-pointer border-0 bg-transparent p-0 shadow-none outline-none transition-transform duration-300 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(113,221,174,0.5)] focus-visible:ring-2 focus-visible:ring-[#71DDAE] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent";

function ProjectTags({ project, isLight }: { project: ProjectCard; isLight: boolean }) {
  const tagClassName = isLight
    ? "rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800"
    : "rounded-full border border-[#71DDAE]/40 bg-[#2A9D7A]/60 px-3 py-1 text-xs font-normal text-black";

  if (project.slug === "jwl-marketing") {
    return (
      <>
        {["Site Vitrine/E-commerce", "SEO", "Design sur-mesure", "API's", "Responsive"].map(
          (tag) => (
            <span key={tag} className={tagClassName}>
              {tag}
            </span>
          ),
        )}
      </>
    );
  }

  if (project.slug === "greenbeamcraft") {
    return (
      <>
        {["Site E-commerce", "Configurateur 2D/3D", "Interface admin", "API's", "Responsive"].map(
          (tag) => (
            <span key={tag} className={tagClassName}>
              {tag}
            </span>
          ),
        )}
      </>
    );
  }

  if (project.slug === "portfolio-noah-descaves") {
    return (
      <>
        {["Site vitrine", "Design sur mesure", "Animations Motion", "Responsive"].map((tag) => (
          <span key={tag} className={tagClassName}>
            {tag}
          </span>
        ))}
      </>
    );
  }

  if (project.slug === "star-limousine-paris") {
    return (
      <>
        {["Création de site", "Site vitrine", "SEO", "Design sur mesure", "Responsive"].map(
          (tag) => (
            <span key={tag} className={tagClassName}>
              {tag}
            </span>
          ),
        )}
      </>
    );
  }

  if (project.slug === "multigestion-secretariat") {
    return (
      <>
        {["Refonte de site", "Site vitrine", "SEO", "Design sur mesure", "Avis Google", "Responsive"].map(
          (tag) => (
            <span key={tag} className={tagClassName}>
              {tag}
            </span>
          ),
        )}
      </>
    );
  }

  if (project.slug === "exemples-bientot") {
    return (
      <>
        {["Refonte", "Création", "Automatisation", "Applications mobiles", "SEO"].map((tag) => (
          <span key={tag} className={tagClassName}>
            {tag}
          </span>
        ))}
      </>
    );
  }

  return (
    <>
      {project.stack.slice(0, 4).map((tag) => (
        <span key={tag} className={tagClassName}>
          {tag}
        </span>
      ))}
    </>
  );
}

function ProjectThumbnail({
  project,
  overlayLabel,
  isLight,
}: {
  project: ProjectCard;
  overlayLabel: string;
  isLight: boolean;
}) {
  return (
    <>
      {project.thumbnail && (
        <Image
          src={project.thumbnail}
          alt={project.title}
          width={1600}
          height={900}
          quality={70}
          sizes={PORTFOLIO_THUMB_SIZES}
          className={`block h-auto w-full align-top ${isLight ? "rounded-2xl" : ""}`}
          loading="lazy"
        />
      )}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
          isLight
            ? "rounded-2xl bg-black/0 group-hover:bg-black/25"
            : "bg-black/0 group-hover:bg-[#71DDAE]/10"
        }`}
      >
        <div
          className={`flex items-center gap-2 rounded-full px-4 py-2 opacity-0 shadow-lg transition-opacity duration-300 group-hover:opacity-100 ${
            isLight
              ? "bg-[#1C1C1C] text-white"
              : "bg-[#71DDAE] backdrop-blur-sm"
          }`}
        >
          <span className={`text-sm font-semibold ${isLight ? "text-white" : "text-[#1C1C1C]"}`}>
            {overlayLabel}
          </span>
          <span className={isLight ? "text-white" : "text-[#1C1C1C]"}>→</span>
        </div>
      </div>
    </>
  );
}

export type PortfolioPreviewSectionProps = {
  /** Projets additionnels (ex. uniquement sur la page `/projets`). */
  extraProjects?: ProjectCard[];
  variant?: SectionVariant;
  /** Remonte la section sous la navbar fixe (pages claires). */
  alignWithNav?: boolean;
};

export function PortfolioPreviewSection({
  extraProjects,
  variant = "dark",
  alignWithNav = false,
}: PortfolioPreviewSectionProps = {}) {
  const theme = sectionSurface(variant);
  const isLight = variant === "light";
  const reducedMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.12 });
  const displayProjects = extraProjects?.length ? [...projects, ...extraProjects] : projects;

  const sectionPadding = alignWithNav
    ? "relative -mt-[calc(5rem+env(safe-area-inset-top))] pt-[calc(5rem+env(safe-area-inset-top)+4rem)] pb-14 sm:pb-16"
    : "py-14 sm:py-16";

  return (
    <section id="portfolio" ref={sectionRef} className={`${theme.section} ${sectionPadding}`}>
      <div className="mx-auto flex max-w-6xl flex-col gap-12 px-6">
        <motion.div
          className="mx-auto flex w-full max-w-3xl flex-col items-center text-center"
          initial={false}
          animate={
            isInView
              ? { opacity: 1, y: 0, filter: "blur(0px)" }
              : { opacity: 0, y: 14, filter: "blur(12px)" }
          }
          transition={entranceTransition(reducedMotion, 0, 0.62)}
        >
          <h2
            className={`${SECTION_TITLE_CLASS} ${
              isLight ? "text-[#1C1C1C]" : theme.heading
            }`}
          >
            Le détail au <span className={HERO_GRADIENT_TEXT}>cœur</span> de chaque{" "}
            <span className={HERO_GRADIENT_TEXT}>projet</span>
          </h2>
        </motion.div>

        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
          {displayProjects.map((project, index) => {
            const cardShell = isLight ? CARD_SHELL_LIGHT : CARD_SHELL_DARK;
            const thumbInteractive = isLight ? THUMB_INTERACTIVE_LIGHT : THUMB_INTERACTIVE_DARK;

            return (
            <motion.article
              key={project.slug}
              className={cardShell}
              initial={false}
              animate={
                isInView
                  ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
                  : { opacity: 0, y: 22, scale: 0.98, filter: "blur(8px)" }
              }
              transition={entranceTransition(
                reducedMotion,
                GRID_DELAY + index * CARD_STAGGER,
                0.55,
              )}
            >
              {project.comingSoon ? (
                <Link href="/offres" className={thumbInteractive}>
                  <ProjectThumbnail project={project} overlayLabel="Voir nos offres" isLight={isLight} />
                </Link>
              ) : project.liveUrl && project.liveUrl !== "#" ? (
                <a
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={thumbInteractive}
                  aria-label={`Ouvrir le site ${project.title} dans un nouvel onglet`}
                >
                  <ProjectThumbnail project={project} overlayLabel="Voir le site" isLight={isLight} />
                </a>
              ) : (
                <div className={`relative block w-full ${isLight ? "overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50" : ""}`}>
                  <ProjectThumbnail project={project} overlayLabel="Voir le site" isLight={isLight} />
                </div>
              )}

              <h3
                className={`text-center text-xl font-bold sm:text-2xl ${isLight ? "text-[#1C1C1C]" : "text-black"}`}
              >
                {project.title}
              </h3>
              <div className="flex flex-wrap justify-center gap-2">
                <ProjectTags project={project} isLight={isLight} />
              </div>
            </motion.article>
            );
          })}
        </div>

        <motion.div
          className="flex justify-center"
          initial={false}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 16, scale: 0.96 }}
          transition={entranceTransition(
            reducedMotion,
            GRID_DELAY + displayProjects.length * CARD_STAGGER + 0.06,
            0.55,
          )}
        >
          <HeroCtaButton>Prendre un RDV</HeroCtaButton>
        </motion.div>
      </div>
    </section>
  );
}
